// SentimentChart.tsx
import { Box, Card, CardContent, Divider, Grid, Stack, Typography } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ERROR, SUCCESS, WARNING } from "src/theme/palette";
import { PieChartDataType } from "src/types/types";
import happyEmoji from "./icons/happy.svg";
import neutralEmoji from "./icons/normal.svg";
import sadEmoji from "./icons/sad.svg";
import Image from "src/components/image";

const colorMap: Record<string, string> = {
  Negative: ERROR.light,
  Neutral: WARNING.light,
  Positive: SUCCESS.light,
};

const emojiMap: Record<string, string> = {
  Negative: sadEmoji,
  Neutral: neutralEmoji,
  Positive: happyEmoji,
};

export default function SentimentChart({ data }: { data: PieChartDataType }) {
  const maxItem = data.content.reduce((prev, current) =>
    current.value > prev.value ? current : prev
  );
  const sum = data.content.reduce((prev, current) => prev + current.value, 0);

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Typography>Sentiment</Typography>
          <Divider
            sx={{
              borderStyle: "dashed",
              opacity: 0.24,
              borderColor: "#0B1D60",
            }}
          />
          <Grid container>
            {/* Left - Breakdown */}
            <Grid item xs={12} md={6}>
              <Stack spacing={2}>
                {data.content.map((item) => (
                  <Box
                    key={item.label}
                    display="flex"
                    alignItems="center"
                    bgcolor={colorMap[item.label] + 33}
                    p={1.5}
                    px={2}
                    borderRadius={2}
                    minWidth={200}
                    gap={1}
                    justifyContent="center"
                  >
                    <Image src={emojiMap[item.label]} sx={{ width: 32, height: 32 }} />
                    <Typography variant="body1" fontWeight="bold" color="text.primary" mr={1}>
                      {((item.value / sum) * 100).toFixed(2)}% ({item.value})
                    </Typography>
                    <Typography variant="body2">{item.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            {/* Right - Gauge */}
            <Grid item xs={12} md={6} alignItems="center">
              <Box position="relative" width="100%" height={240}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={data.content}
                      dataKey="value"
                      startAngle={180}
                      endAngle={0}
                      innerRadius={100}
                      outerRadius={120}
                      paddingAngle={5}
                    >
                      {data.content.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={colorMap[entry.label]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {/* Center icon and title */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  textAlign="center"
                  style={{ transform: "translate(-50%, -50%)" }}
                >
                  <Image
                    src={emojiMap[maxItem.label]}
                    sx={{
                      margin: "auto",
                      width: 55,
                      height: 55,
                      bgcolor: colorMap[maxItem.label] + 33,
                      padding: 1,
                      borderRadius: "50%",
                    }}
                  />
                  <Typography fontWeight="bold" mt={1}>
                    {maxItem.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {data.title}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Stack>
      </CardContent>
    </Card>
  );
}
